import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { deleteTheme, getAvailableThemes, getDefaultUserTheme } from '../../api/resource';
import { ThemeEditorInfo } from '../models/state.model';
import { AvailableTheme } from '../../models/profile';
import { getSystemTheme } from './theme-reducer';

const initialState: ThemeEditorInfo = {
  mode: 'create',
  themeId: null,
  availableThemes: [],
  currentTheme: null,
  loading: false,
  deleteLoading: false,
};

type ThemeItem = Omit<AvailableTheme, 'scope'>;

const transformThemesData = (data: any): AvailableTheme[] => {
  return Object.entries(data).flatMap(
    ([parentKey, items]) =>
      (items as ThemeItem[])?.map((item) => ({
        ...item,
        scope: parentKey,
      })) ?? [],
  );
};

export const fetchAvailableThemes = createAsyncThunk('theme/fetchAvailableThemes', async () => {
  const res = await getAvailableThemes();
  return transformThemesData(res.data);
});

export const fetchCurrentTheme = createAsyncThunk('theme/fetchCurrentTheme', async () => {
  const theme = getSystemTheme();
  const res = await getDefaultUserTheme(theme);
  return res.data;
});

export const fetchDeleteTheme = createAsyncThunk(
  'theme/deleteTheme',
  async (themeId: string, { rejectWithValue }) => {
    try {
      await deleteTheme(themeId);
      return themeId;
    } catch (error) {
      return rejectWithValue('Ошибка при удалении темы');
    }
  },
);

const themeEditorSlice = createSlice({
  name: 'themeEditor',
  initialState,
  reducers: {
    setCreateMode(state) {
      state.mode = 'create';
      state.themeId = null;
    },
    setEditMode(state, action: PayloadAction<string>) {
      state.mode = 'edit';
      state.themeId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAvailableThemes.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchAvailableThemes.fulfilled, (state, action) => {
      state.availableThemes = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchAvailableThemes.rejected, (state) => {
      state.availableThemes = [];
      state.loading = false;
    });
    builder.addCase(fetchCurrentTheme.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(fetchCurrentTheme.fulfilled, (state, action) => {
      state.currentTheme = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchCurrentTheme.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(fetchDeleteTheme.pending, (state) => {
      state.deleteLoading = true;
    });
    builder.addCase(fetchDeleteTheme.fulfilled, (state, action) => {
      state.availableThemes = state.availableThemes.filter((theme) => theme.id !== action.payload);
      state.deleteLoading = false;
    });
    builder.addCase(fetchDeleteTheme.rejected, (state) => {
      state.deleteLoading = false;
    });
  },
});

export const { setCreateMode, setEditMode } = themeEditorSlice.actions;

const themeEditorReducer = themeEditorSlice.reducer;

export default themeEditorReducer;
